import { NO_IFRAME_SOURCES } from '@/types';

export function formatTime(ts: string | undefined): string {
  if (!ts) return '';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  return d.toLocaleDateString();
}

export function formatTimeAgo(date: Date): string {
  const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);
  if (seconds < 30) return 'just now';
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  return `${Math.floor(seconds / 3600)}h ago`;
}

export function formatRelativeTime(ts: string | undefined): string {
  if (!ts) return 'never';
  const d = new Date(ts);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins} minutes ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
  const diffDays = Math.floor(diffMins / 1440);
  if (diffDays === 1) return 'yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  return d.toLocaleDateString();
}

export function escapeHtml(text: string | undefined): string {
  if (!text) return '';
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}

export function canIframe(url: string | undefined): boolean {
  if (!url) return false;
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    return !NO_IFRAME_SOURCES.some(blocked => hostname.includes(blocked));
  } catch {
    return false;
  }
}

export function getScoreColor(score: number, threshold: number): string {
  const percent = Math.round(score * 100);
  if (percent >= threshold) return 'from-green-500 to-green-400';
  if (percent >= 50) return 'from-sabio-yellow to-sabio-yellow-dark';
  return 'from-gray-500 to-gray-400';
}

export function getScoreTextColor(score: number, threshold: number): string {
  const percent = Math.round(score * 100);
  if (percent >= threshold) return 'text-green-400';
  if (percent >= 50) return 'text-sabio-yellow';
  return 'text-gray-400';
}

export function getSeverityClass(severity: string): string {
  switch (severity) {
    case 'critical': return 'severity-critical';
    case 'high': return 'severity-high';
    case 'medium': return 'severity-medium';
    case 'low': return 'severity-low';
    default: return 'severity-none';
  }
}

export function cn(...classes: (string | boolean | undefined)[]): string {
  return classes.filter(Boolean).join(' ');
}

/**
 * Format an expires_at timestamp into a human-readable expiry label.
 * Returns null if no date provided.
 */
export function formatExpiry(expiresAt: string): { label: string; urgent: boolean } | null {
  if (!expiresAt) return null;
  const expires = new Date(expiresAt);
  const now = new Date();
  const diffMs = expires.getTime() - now.getTime();

  // Already expired
  if (diffMs <= 0) {
    return { label: 'expired', urgent: true };
  }

  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffHours / 24);

  if (diffHours < 1) {
    const diffMins = Math.floor(diffMs / 60000);
    return { label: `${diffMins}m left`, urgent: true };
  }
  if (diffHours < 24) {
    return { label: `${diffHours}h left`, urgent: diffHours < 6 };
  }
  if (diffDays === 1) {
    return { label: '1d left', urgent: false };
  }
  return { label: `${diffDays}d left`, urgent: false };
}
